import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowUpRight, Radio } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (dateStr, lang) => {
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  try {
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
    if (days < 1) return rtf.format(0, "day");
    if (days < 30) return rtf.format(-days, "day");
    return rtf.format(-Math.floor(days / 30), "month");
  } catch {
    return d.toLocaleDateString();
  }
};

export const NewsSection = () => {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/widgets/news`, { params: { lang } }).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [lang]);

  if (!t.news || items.length === 0) return null;

  return (
    <section className="py-20 sm:py-24 bg-[#EFF4F8] border-y border-[#D8E2EC]" data-testid="news-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#1E6FA5] font-semibold">{t.news.eyebrow}</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight text-[#142D44]">{t.news.title}</h2>
            <p className="mt-3 text-sm text-[#4A5568] max-w-xl">{t.news.sub}</p>
          </div>
          <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#15803D]">
            <Radio size={13} className="animate-pulse" /> {t.news.updated}
          </p>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.slice(0, 6).map((n, i) => (
            <a
              key={i}
              href={n.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`news-item-${i}`}
              className="group bg-white border border-[#D8E2EC] rounded-2xl hover:border-[#1E6FA5]/50 hover:shadow-md transition-all duration-300 p-7 flex flex-col justify-between min-h-[180px]"
            >
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1E6FA5]">{n.source || "Cyprus"} · {timeAgo(n.date, lang)}</p>
                <p className="mt-3 text-[15px] font-medium text-[#142D44] leading-snug group-hover:text-[#1E6FA5] transition-colors">{n.title.replace(/ - [^-]+$/, "")}</p>
              </div>
              <ArrowUpRight size={18} className="mt-5 text-[#9AB2C6] group-hover:text-[#B85D2A] transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};
